import Link from "next/link";

import {
  Anchor,
  Button,
  FormativeCheckSVG,
  Icon,
  LineClamp,
  PowerWordFilledSVG,
  SpanishFilledSVG,
  useModal,
} from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import dayjs from "dayjs";
import { useRouter } from "next/router";

import AdminContentSettingsCardFooter from "@/admin/AdminContentSettingsCardFooter";
import { OWN_ORIGIN } from "@/base/base.constants";
import AssignModal from "@/components/AssignModal";
import VISIBILITY_MARKER_REASONS from "@/constants/VISIBILITY_MARKER_REASONS";
import {
  CONTENT_CARD_DATE_TIME_FORMAT,
  ContentTypesEnum,
  DISABLED_ASSIGNMENT_ACTIONS_TEXT_SET_TYPES,
} from "@/search/NewSearchPageMVP/SearchResults/ContentCard/ContentCard.constants";
import {
  addToAssignmentButtonCss,
  cardActionsContainerCss,
  contentCardBadgesContainerCss,
  contentCardBottomSectionContainerCss,
  contentCardDateAndBadgesContainerCss,
  contentCardDateCss,
  contentCardDescriptionCss,
  contentCardFormativeBadgeCss,
  contentCardGradeAndLexileCss,
  contentCardInformationContainer,
  contentCardPowerWordBadgeCss,
  contentCardSpanishBadgeCss,
  contentCardTagCss,
  contentCardTagsContainerCss,
  contentCardTitleCss,
  contentSettingsModeCss,
  linkCss,
  spacedBetweenFlexColumnCss,
} from "@/search/NewSearchPageMVP/SearchResults/ContentCard/ContentCard.styles";
import {
  canUserSeeReadingSkills,
  getAssignHref,
  getFormattedGradeLevelLabel,
  getFormattedLexileLevelLabel,
  getReadingSkillsLabels,
} from "@/search/NewSearchPageMVP/SearchResults/ContentCard/ContentCardBottomSection/ContentCardBottomSection.utils";
import { IContentSettingsGroup } from "@/search/NewSearchPageMVP/types/IContentSettingsGroup";
import { ISearchResult } from "@/search/NewSearchPageMVP/types/ISearchResult";
import getUserProperties from "@/users/getUserProperties";
import useUser from "@/users/useUser";

const AnchorWithTrackEvent = withTrackEvent(Anchor);

const ContentCardBottomSection = ({
  content,
  contentSettingsGroups,
}: IContentCardBottomSection) => {
  const { data: user } = useUser();
  const { asPath } = useRouter();

  const {
    url = "",
    title = "",
    label = "",
    content_id: contentId,
    date_lite_product_grant_expires: dateLiteProductGrantExpires,
    description = "",
    content_type: contentType,
    date_published: datePublished,
    grade_level: gradeLevel = [],
    lexile_levels: lexileLevels = [],
    reading_skills: readingSkills = [],
    has_formative: hasFormative,
    visibility_markers: visibilityMarkers = [],
    features: {
      has_power_words: hasPowerWords,
      available_in_spanish: availableInSpanish,
    } = {},
  } = content;

  const { isLiteUser, isParent, isStudent, activeELALicense, canSeeFormative } =
    getUserProperties(user, { shouldUseNewUserApi: false });

  const isFormative = hasFormative?.active;
  const showFormativeIcon = isFormative && canSeeFormative;

  const returnUrl = `${OWN_ORIGIN}${asPath}`;

  const canSeeReadingSkills = canUserSeeReadingSkills({
    activeELALicense,
    isLiteUser,
  });
  const shouldShowReadingSkills =
    canSeeReadingSkills && !!readingSkills?.length;

  const isWorksheet = contentType === ContentTypesEnum.externallink;
  const isFreeArticle = isLiteUser && !!dateLiteProductGrantExpires;

  const showAddToAssignmentOption = contentType !== ContentTypesEnum.textset;
  const showAssignmentActions =
    !isStudent &&
    !isParent &&
    (!isLiteUser || isFreeArticle) &&
    !isWorksheet &&
    !(
      contentType === ContentTypesEnum.textset &&
      DISABLED_ASSIGNMENT_ACTIONS_TEXT_SET_TYPES[label]
    ) &&
    !visibilityMarkers.find(
      (marker) => marker.reason === VISIBILITY_MARKER_REASONS.PREMIUM_CONTENT,
    );

  const formattedMaturityGradeLevelLabel =
    getFormattedGradeLevelLabel(gradeLevel);
  const formattedReadingSkillsLabel =
    getFormattedLexileLevelLabel(lexileLevels);

  const [modalProps, setModalActive] = useModal(false);

  const closeModal = () => {
    setModalActive(false);
  };

  const contentIdsToAssign = [contentId];
  const createAssignmentUrl = `/assignments/create?return_url=${returnUrl}&createdFrom=${contentId}&contentIds=${contentId}`;

  const isContentSettingsMode =
    Array.isArray(contentSettingsGroups) && contentSettingsGroups.length > 0;

  return (
    <div css={spacedBetweenFlexColumnCss}>
      <div
        data-testid="content-card-bottom"
        css={contentCardBottomSectionContainerCss}
      >
        <div css={contentCardDateAndBadgesContainerCss}>
          <time dateTime={datePublished} css={contentCardDateCss}>
            {dayjs(datePublished).format(CONTENT_CARD_DATE_TIME_FORMAT)}
          </time>

          <ul
            role="list"
            aria-label="Article attachments"
            css={contentCardBadgesContainerCss}
          >
            {showFormativeIcon && (
              <li role="listitem">
                <Icon
                  title="Formative available"
                  __cssFor={contentCardFormativeBadgeCss}
                  SvgComponent={FormativeCheckSVG}
                />
              </li>
            )}

            {hasPowerWords && activeELALicense && (
              <li role="listitem">
                <Icon
                  isDecorative={false}
                  title="Power words available"
                  __cssFor={contentCardPowerWordBadgeCss}
                  SvgComponent={PowerWordFilledSVG}
                />
              </li>
            )}

            {availableInSpanish && (
              <li role="listitem">
                <Icon
                  isDecorative={false}
                  title="Also available in spanish"
                  __cssFor={contentCardSpanishBadgeCss}
                  SvgComponent={SpanishFilledSVG}
                />
              </li>
            )}
          </ul>
        </div>
        <div css={contentCardInformationContainer}>
          <h3 css={contentCardTitleCss}>
            <LineClamp innerElement="span" lines={3} text={title || ""} />
          </h3>

          <p css={contentCardDescriptionCss}>
            <LineClamp
              innerElement="span"
              lines={2}
              text={description || "&nbsp;"}
            />
          </p>
        </div>

        <div css={contentCardGradeAndLexileCss}>
          {formattedMaturityGradeLevelLabel && (
            <p>
              Maturity: <strong>{formattedMaturityGradeLevelLabel}</strong>
            </p>
          )}
          {formattedReadingSkillsLabel && (
            <p>
              Reading: <strong>{formattedReadingSkillsLabel}</strong>
            </p>
          )}
        </div>

        {shouldShowReadingSkills && (
          <ul
            role="list"
            aria-label="Article tags"
            css={contentCardTagsContainerCss}
          >
            {getReadingSkillsLabels(readingSkills).map((label) => (
              <li
                role="listitem"
                data-testid="reading-skill-tag"
                key={label}
                css={contentCardTagCss}
              >
                {label}
              </li>
            ))}
          </ul>
        )}
      </div>

      {isContentSettingsMode && (
        <AdminContentSettingsCardFooter
          contentId={contentId}
          contentSettingsGroups={contentSettingsGroups}
          visibilityMarkers={visibilityMarkers}
          style={contentSettingsModeCss}
        />
      )}

      {isWorksheet && (
        <div css={cardActionsContainerCss}>
          <AnchorWithTrackEvent
            openInNewTab
            href={url}
            __cssFor={linkCss}
            actionName={actionNames.navigate}
            objectType={objectTypes.assignment}
            streamProperties={{ content_type: contentType }}
            __renderCustomLink={({ children, href, ...anchorProps }) => {
              return (
                <Link href={href as URL} passHref legacyBehavior>
                  <a {...anchorProps}>{children}</a>
                </Link>
              );
            }}
          >
            Open in new tab
          </AnchorWithTrackEvent>
        </div>
      )}

      {showAssignmentActions && (
        <div css={cardActionsContainerCss}>
          <AnchorWithTrackEvent
            __cssFor={linkCss}
            href={getAssignHref(returnUrl, contentId)}
            actionName={actionNames.navigate}
            objectType={objectTypes.assignment}
            streamProperties={{ content_type: contentType }}
            __renderCustomLink={({ children, href, ...anchorProps }) => {
              return (
                <Link href={href as URL} passHref legacyBehavior>
                  <a {...anchorProps}>{children}</a>
                </Link>
              );
            }}
          >
            Assign
          </AnchorWithTrackEvent>

          {showAddToAssignmentOption && (
            <Button
              onClick={() => setModalActive(true)}
              flavor={Button.flavors.incognito}
              __cssFor={addToAssignmentButtonCss}
            >
              Add to assignment
            </Button>
          )}
        </div>
      )}
      {modalProps.isActive && (
        <AssignModal
          contentMemberIds={contentIdsToAssign}
          createAssignmentUrl={createAssignmentUrl}
          modalProps={modalProps}
          handleClickToCloseModal={closeModal}
        />
      )}
    </div>
  );
};

interface IContentCardBottomSection {
  content: ISearchResult;
  contentSettingsGroups: IContentSettingsGroup[];
}

export { ContentCardBottomSection };
